import type { JSX } from 'react';
import { Navigate } from 'react-router-dom';

import FaqPage from '@/app/pages/FaqPage/FaqPage';
import MarketDetailsPage from '@/app/pages/MarketDetailsPage/MarketDetailsPage';
import VaultsItemRedirect from '@/app/pages/MarketDetailsPage/VaultsItemRedirect';
import { NotFoundPage } from '@/app/pages/NotFoundPage/NotFoundPage';
import PortfolioStatsPage from '@/app/pages/PortfolioStatsPage/PortfolioStatsPage';
import PrivacyPolicyPage from '@/app/pages/PrivacyPolicyPage/PrivacyPolicyPage';
import ReferralPage from '@/app/pages/ReferralPage/ReferralPage';
import TermOfServicePage from '@/app/pages/TermOfServicePage/TermOfServicePage';
import VaultsPage from '@/app/pages/VaultsPage/VaultsPage';
import { commonRoutes } from '@/app/providers/router/routes';
import { AppEnv, appEnv } from '@/app/shared/constants/env';

export { commonRoutes, externalRoutes, getMarketDetailsPath } from '@/app/providers/router/routes';

export interface AppRoute {
  path: string;
  element: JSX.Element;
  label: string;
  showInHeader?: boolean;
  showInFooter?: boolean;
  enabledEnvs?: AppEnv[];
}

const filterRoutesByEnv = (routes: Record<string, AppRoute>, currentEnv: AppEnv): Record<string, AppRoute> => {
  return Object.fromEntries(
    Object.entries(routes).filter(([, route]) => {
      if (!route.enabledEnvs) return true;
      return route.enabledEnvs.includes(currentEnv);
    }),
  );
};

const rawRoutes: Record<string, AppRoute> = {
  [commonRoutes.HOME]: {
    path: commonRoutes.HOME,
    element: <Navigate to={commonRoutes.VAULTS} replace />,
    label: '',
    showInHeader: false,
  },
  [commonRoutes.VAULTS]: {
    path: commonRoutes.VAULTS,
    element: <VaultsPage />,
    label: 'Vaults',
    showInHeader: true,
  },
  [commonRoutes.VAULTS_ITEM]: {
    path: commonRoutes.VAULTS_ITEM,
    element: <VaultsItemRedirect />,
    label: '',
    showInHeader: false,
  },
  [commonRoutes.MARKET_DETAILS]: {
    path: commonRoutes.MARKET_DETAILS,
    element: <MarketDetailsPage />,
    label: '',
    showInHeader: false,
  },
  [commonRoutes.PORTFOLIO]: {
    path: commonRoutes.PORTFOLIO,
    element: <PortfolioStatsPage />,
    label: 'Portfolio',
    showInHeader: true,
  },
  [commonRoutes.REFERRAL]: {
    path: commonRoutes.REFERRAL,
    element: <ReferralPage />,
    label: 'Points',
    showInHeader: true,
    enabledEnvs: ['develop', 'stage'],
  },
  [commonRoutes.FAQ]: {
    path: commonRoutes.FAQ,
    element: <FaqPage />,
    label: 'FAQ',
    showInHeader: true,
  },
  [commonRoutes.NOT_FOUND]: {
    path: commonRoutes.NOT_FOUND,
    element: <NotFoundPage />,
    label: 'Not Found',
  },
  [commonRoutes.TERM_OF_SERVICE]: {
    path: commonRoutes.TERM_OF_SERVICE,
    element: <TermOfServicePage />,
    label: 'Term of Service',
    showInHeader: false,
  },
  [commonRoutes.PRIVACY_POLICY]: {
    path: commonRoutes.PRIVACY_POLICY,
    element: <PrivacyPolicyPage />,
    label: 'Privacy Policy',
    showInHeader: false,
  },
};

export const appRoutes = filterRoutesByEnv(rawRoutes, appEnv as AppEnv);

export const getHeaderRoutes = () => Object.values(appRoutes).filter((route) => route.showInHeader);
